'use client';
// /jobs = 고용지원금 자동진단 랜딩 — 제공 디자인('고용지원금' nav 클릭 화면) 복제.
//   ⚠ 홈(/)·공유 컴포넌트는 수정 금지. 이 파일은 /jobs 전용.
//   히어로 → 4단계 안내 → 진단 대상 6개 제도 → (CTA) 실제 계산기(embedded)로 스크롤.
import { useRouter } from 'next/navigation';
import Icon from '@/components/vster/Icon';
import { c } from '@/lib/vster/theme';

const STEPS = [
  { n: '01', icon: 'business', title: '기업정보 입력', desc: '지역·업종·직원 수 등 간단한 기업정보를 입력합니다.' },
  { n: '02', icon: 'person_add', title: '채용조건 입력', desc: '채용 예정 직원 또는 현재 직원의 조건을 입력합니다.' },
  { n: '03', icon: 'manage_search', title: '지원금 자동진단', desc: '조건에 맞는 고용지원금과 예상 지원금액을 확인합니다.' },
  { n: '04', icon: 'assignment_ind', title: '전문가 상담', desc: '정확한 대상 여부와 신청방법이 궁금하다면 노무사 상담을 신청합니다.' },
] as const;

const SCHEMES = [
  { name: '청년일자리도약장려금', max: '최대 720만원', req: '만 15~34세 취업애로청년을 정규직으로 6개월 이상 고용' },
  { name: '고용촉진장려금', max: '최대 720만원', req: '취업지원프로그램을 이수한 취업 취약계층을 신규 채용' },
  { name: '고령자 고용지원금', max: '최대 360만원', req: '만 60세 이상 근로자의 고용이 직전 3년 평균보다 증가한 기업' },
  { name: '고령자 계속고용장려금', max: '최대 720만원', req: '정년이 도래한 근로자를 계속고용제도로 계속 고용하는 기업' },
  { name: '출산육아기 고용안정장려금 (대체인력)', max: '최대 960만원', req: '출산휴가·육아휴직 등에 따라 대체인력을 채용한 기업' },
  { name: '워라밸일자리 장려금', max: '최대 480만원', req: '기존 근로자의 소정근로시간 단축제도를 운영하는 기업' },
] as const;

export default function JobsLanding({ loggedIn }: { loggedIn: boolean }) {
  // '내 고용지원금 확인하기' → 진단(/jobs/diagnose/1)은 로그인 필요.
  //   비로그인 시 로그인 페이지로 보내고, 로그인 후 진단 1단계로 복귀(callbackUrl).
  const router = useRouter();
  const startDiagnose = () =>
    router.push((loggedIn ? '/jobs/diagnose/1' : '/login?callbackUrl=' + encodeURIComponent('/jobs/diagnose/1')) as never);
  const ctaBtn = (
    <button onClick={startDiagnose} style={{ display: 'inline-flex', alignItems: 'center', gap: 8, background: c.action, color: '#fff', border: 'none', borderRadius: 12, padding: '15px 26px', fontFamily: 'inherit', fontSize: 16, fontWeight: 700, cursor: 'pointer', boxShadow: '0 4px 14px -4px rgba(44,92,255,.5)' }}>
      <Icon name="manage_search" size={20} color="#fff" />내 고용지원금 확인하기
    </button>
  );

  return (
    <main style={{ background: c.bg, color: c.ink, animation: 'vfade .35s ease' }}>
      {/* ── 히어로 ── */}
      <section style={{ background: 'linear-gradient(180deg,#FBFCFE 0%,#fff 60%)', borderBottom: `1px solid ${c.line}` }}>
        <div style={{ maxWidth: 1180, margin: '0 auto', padding: '72px 24px 60px' }}>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, color: c.actionDark, fontSize: 12, fontWeight: 700, letterSpacing: '.12em', marginBottom: 18 }}>
            <span style={{ width: 22, height: 1, background: c.action }} />고용지원금 자동진단
          </span>
          <h1 className="vh1" style={{ fontSize: 46, lineHeight: 1.15, letterSpacing: '-.04em', fontWeight: 800, margin: '0 0 18px', color: c.ink }}>
            직원 채용 전,<br />받을 수 있는 지원금부터 확인하세요
          </h1>
          <p style={{ fontSize: 17, lineHeight: 1.65, color: c.muted, margin: '0 0 30px', maxWidth: 560 }}>
            몇 가지 조건만 입력하면 우리 회사가 받을 가능성이 있는 고용지원금과 예상 금액을 확인할 수 있습니다.
          </p>
          <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
            {ctaBtn}
            <span style={{ fontSize: 13.5, color: c.muted }}>무료 · 약 1분</span>
          </div>
        </div>
      </section>

      {/* ── 4단계 안내 ── */}
      <section style={{ maxWidth: 1180, margin: '0 auto', padding: '52px 24px 44px' }}>
        <h2 className="vh2" style={{ fontSize: 30, fontWeight: 800, letterSpacing: '-.03em', margin: '0 0 8px', color: c.ink }}>복잡한 고용지원금, 간단하게 확인하세요</h2>
        <p style={{ fontSize: 15, color: c.muted, margin: '0 0 24px' }}>네 단계로 진단부터 전문가 상담까지 이어집니다</p>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(240px,1fr))', gap: 14 }}>
          {STEPS.map((s) => (
            <div key={s.n} style={{ background: c.surface, border: `1px solid ${c.line}`, borderRadius: 14, padding: 22, display: 'flex', flexDirection: 'column', gap: 10 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <span style={{ width: 30, height: 30, borderRadius: 8, background: c.ink, color: '#fff', fontSize: 13, fontWeight: 800, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{s.n}</span>
                <Icon name={s.icon} size={22} color={c.action} />
              </div>
              <h3 style={{ fontSize: 18, fontWeight: 700, letterSpacing: '-.02em', margin: '2px 0 0', color: c.ink }}>{s.title}</h3>
              <p style={{ fontSize: 14, lineHeight: 1.6, color: c.muted, margin: 0 }}>{s.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ── 진단 대상 6개 제도 ── */}
      <section style={{ background: c.surfaceAlt, borderTop: `1px solid ${c.line}`, borderBottom: `1px solid ${c.line}` }}>
        <div style={{ maxWidth: 1180, margin: '0 auto', padding: '52px 24px' }}>
          <h2 className="vh2" style={{ fontSize: 26, fontWeight: 800, letterSpacing: '-.03em', margin: '0 0 6px', color: c.ink }}>진단 대상 고용지원금</h2>
          <p style={{ fontSize: 15, color: c.muted, margin: '0 0 22px' }}>아래 6개 제도를 우리 회사 조건과 대조합니다</p>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(300px,1fr))', gap: 12 }}>
            {SCHEMES.map((s) => (
              <div key={s.name} style={{ background: c.surface, border: `1px solid ${c.line}`, borderRadius: 12, padding: '18px 20px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 12, marginBottom: 7 }}>
                  <span style={{ fontSize: 16, fontWeight: 700, color: c.ink, letterSpacing: '-.01em' }}>{s.name}</span>
                  <span className="num" style={{ flexShrink: 0, fontSize: 14, fontWeight: 800, color: c.actionDark, whiteSpace: 'nowrap' }}>{s.max}</span>
                </div>
                <p style={{ fontSize: 13.5, lineHeight: 1.55, color: c.muted, margin: 0 }}>{s.req}</p>
              </div>
            ))}
          </div>
          <div style={{ display: 'flex', justifyContent: 'center', marginTop: 28 }}>{ctaBtn}</div>
        </div>
      </section>
    </main>
  );
}
